import React from 'react'
import PropTypes from 'prop-types'
import { debounce } from 'throttle-debounce'

import ValidateEmail from '../../../Utils/ValidateEmail'
import Icon from '../Icon/Icon'

import UserResults from './UserResults'
import SelectedUsers from './SelectedUsers'

class UserSearch extends React.Component {
  constructor(props) {
    super(props)

    this.state = this.getDefaultSettings()

    this.userSearchRef = React.createRef()

    this.fetchUsers = debounce(200, this.fetchUsers)

    this.onTermChange = this.onTermChange.bind(this)
    this.onSubmitSuccess = this.onSubmitSuccess.bind(this)
    this.onSubmitFailure = this.onSubmitFailure.bind(this)
    this.selectUser = this.selectUser.bind(this)
    this.removeUser = this.removeUser.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleKeyPress = this.handleKeyPress.bind(this)
    this.removeGuest = this.removeGuest.bind(this)
    this.updateActiveUser = this.updateActiveUser.bind(this)
    this.showResults = this.showResults.bind(this)
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
    document.addEventListener('keydown', this.handleKeyPress)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
    document.removeEventListener('keydown', this.handleKeyPress)
  }

  componentDidUpdate(prevProps) {
    if (this.props.scope !== prevProps.scope || this.props.type !== prevProps.type) {
      const newState = this.getDefaultSettings()
      this.setState(newState, this.props.clearAllUsers)
    }

    if (this.props.showError !== prevProps.showError) {
      this.setState({ showError: this.props.showError })
    }
  }

  getDefaultSettings() {
    return {
      term: '',
      users: [],
      selectedUsers: [],
      emails: [],
      isEmail: false,
      showResults: false,
      showLoader: true,
      showError: this.props.showError,
      errorText: 'This field is required',
      cursorPosition: -1,
      arrowNav: false,
      activeUser: {},
    }
  }

  fetchUsers() {
    axios.get(`/api/internal/users/search-${this.props.scope}?term=${this.state.term}`)
      .then(this.onSubmitSuccess)
      .catch(this.onSubmitFailure)
  }

  onSubmitSuccess(response) {
    this.setState({
      showResults: true,
      showLoader: false,
      users: response.data.data,
    })
  }

  onSubmitFailure() {
    this.setState({
      showResults: false,
      cursorPosition: -1,
      showError: true,
      errorText: 'Something went wong, please try again',
    })
  }

  onTermChange(event) {
    const value = event.target.value
    const isEmail = ValidateEmail(value)

    this.setState({
      term: value,
      isEmail,
      showResults: true,
      showLoader: true,
    }, () => {
      if (value.length > 2) {
        this.fetchUsers(value)
      }
    })
  }

  selectUser(user) {
    this.setState(prevState => ({
      selectedUsers: [...prevState.selectedUsers, user],
      showResults: false,
      cursorPosition: -1,
      term: '',
      showError: false,
    }))

    this.props.updateUsersList(user.id, 'users')
  }

  selectGuest(email) {
    if (!this.state.emails.includes(email)) {
      this.setState(prevState => ({
        emails: [...prevState.emails, email],
        showResults: false,
        cursorPosition: -1,
        term: '',
        showError: false,
      }))

      this.props.updateUsersList(email, 'emails')
    }
  }

  updateActiveUser(user) {
    this.setState({ activeUser: user })
  }

  removeUser(user) {
    this.setState(prevState => ({
      selectedUsers: prevState.selectedUsers.filter(currUser => currUser.id !== user.id),
    }))

    this.props.removeUser(user.id, 'users')
  }

  removeGuest(email) {
    this.setState(prevState => ({
      emails: prevState.emails.filter(currEmail => currEmail !== email),
    }))

    this.props.removeUser(email, 'emails')
  }

  showResults() {
    if (this.state.users.length > 0) {
      this.setState({ showResults: true })
    }
  }

  handleClickOutside(event) {
    if (!this.userSearchRef.current.contains(event.target)) {
      this.setState({
        showResults: false,
        cursorPosition: -1,
      })
    }
  }

  handleKeyPress(event) {
    // ESC closes search results
    if (event.keyCode === 27) {
      this.setState({
        showResults: false,
        cursorPosition: -1,
      })
    }
    // Arrow up/down button should select next/previous list element
    if (event.keyCode === 38 && this.state.showResults) {
      this.setState({ arrowNav: true })

      if (this.state.cursorPosition > 0) {
        this.setState(prevState => ({ cursorPosition: prevState.cursorPosition - 1 }))
      }
    } else if (event.keyCode === 40 && this.state.showResults) {
      this.setState({ arrowNav: true })

      if (this.state.cursorPosition < this.state.users.length - 1) {
        this.setState(prevState => ({ cursorPosition: prevState.cursorPosition + 1 }))
      }
    }
    // Enter/Return handles user select or email add
    if (this.state.showResults && event.keyCode === 13) {
      event.preventDefault()

      if (this.state.isEmail) {
        this.state.term && this.selectGuest(this.state.term)
      } else if (this.state.showResults && this.state.cursorPosition > -1 && this.state.activeUser) {
        this.selectUser(this.state.activeUser)
      }
    }
  }

  render() {
    const inputErrorClass = this.state.showError && this.state.errorText ? 'is-invalid' : ''

    return (
      <div className={`user-search form-group ${this.props.formClassName}`} ref={this.userSearchRef}>
        {this.props.label && (
          <label htmlFor={this.props.id}>{this.props.label}{this.props.scope === 'buyers' && <b> *</b>}</label>
        )}
        <div className="form-group--with-prefix">
          <div className="form-group-prepend">
            <span className={`form-group-text ${inputErrorClass}`}>
              <Icon type={['far', 'user']} notSpaced />
            </span>
          </div>
          <input
            className={`form-control ${this.props.inputClassName} ${inputErrorClass}`}
            id={this.props.id}
            type="text"
            name={this.props.name}
            value={this.state.term}
            onChange={this.onTermChange}
            onClick={this.showResults}
            placeholder={this.props.placeholder}
            autoComplete="off"
          />
        </div>

        {this.state.showError && (
          <div className="error-message">
            <span className="text">{this.state.errorText}</span>
          </div>
        )}

        {this.state.term.length > 0 && this.state.showResults && (
          <UserResults
            users={this.state.users}
            scope={this.props.scope}
            selectedUsers={this.state.selectedUsers}
            selectUser={this.selectUser}
            showLoader={this.state.showLoader}
            isEmail={this.state.isEmail}
            cursorPosition={this.state.cursorPosition}
            updateActiveUser={this.updateActiveUser}
          />
        )}

        {(this.state.selectedUsers.length > 0 || this.state.emails.length > 0) && (
          <div className="user-search__selected-users">
            <SelectedUsers
              users={this.state.selectedUsers}
              emails={this.state.emails}
              scope={this.props.scope}
              removeUser={this.removeUser}
              removeGuest={this.removeGuest}
            />
          </div>
        )}
      </div>
    )
  }
}

UserSearch.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  scope: PropTypes.string.isRequired,
  label: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.bool,
  formClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  placeholder: PropTypes.string,
  updateUsersList: PropTypes.func.isRequired,
  removeUser: PropTypes.func.isRequired,
  clearAllUsers: PropTypes.func.isRequired,
}

UserSearch.defaultProps = {
  label: null,
  name: 'user-search',
  required: false,
  formClassName: 'form-group-grey',
  inputClassName: 'form-control-grey',
  placeholder: '',
}

export default UserSearch
